import React, { useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Button,
  Spin,
  message,
  Alert,
  Card,
} from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getLocationGenericMappings,
  createLocationGenericMapping,
  getLocations,
} from '../../api/location';

const { Title, Text } = Typography;

type StatusType = 'mapped' | 'needs-mapping';

function getStatusFromCalyxCountry(calyxCountry: string | null | undefined): StatusType {
  return calyxCountry ? 'mapped' : 'needs-mapping';
}

function renderStatusTag(status: StatusType) {
  if (status === 'mapped') return <Tag color="green">Mapped</Tag>;
  return <Tag color="red">Needs Mapping</Tag>;
}

const STAT_CARD_SOFT_BG: Record<string, string> = {
  '#8c8c8c': '#f4f4f4',
  '#52c41a': '#f0fbe8',
  '#ff4d4f': '#fff1f1',
  '#1677ff': '#eef5ff',
};

function StatCards({
  stats,
}: {
  stats: { label: string; value: number; color: string; active?: boolean; onClick?: () => void }[];
}) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
      {stats.map((stat) => (
        <div
          key={stat.label}
          onClick={stat.onClick}
          style={{
            flex: '1 1 128px',
            minWidth: 128,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            background: stat.active ? STAT_CARD_SOFT_BG[stat.color] || '#f5f5f5' : '#fff',
            borderTop: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderRight: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderBottom: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderLeft: `3px solid ${stat.color}`,
            borderRadius: 8,
            padding: '7px 12px',
            cursor: stat.onClick ? 'pointer' : 'default',
            transition: 'background 0.15s, border-color 0.15s',
          }}
        >
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            {stat.label}
          </Text>
          <Text strong style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>
            {stat.value}
          </Text>
        </div>
      ))}
    </div>
  );
}

async function fetchLocationGenericMappingsData() {
  const data = await getLocationGenericMappings();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows.map((row: any, index: number) => ({
    ...row,
    id: row.id ?? `${row.registry_country || 'REG'}-${index}`,
    project_count:
      typeof row.project_count === 'string'
        ? Number(row.project_count)
        : row.project_count,
  }));
}

async function fetchLocationsData() {
  const data = await getLocations();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows;
}

async function updateLocationGenericMapping(record: any, calyxCountry: string | null) {
  const payload = {
    registry_country: record.registry_country,
    calyx_country: calyxCountry,
  };

  await createLocationGenericMapping(payload);

  return {
    ...record,
    calyx_country: calyxCountry,
  };
}

const locationGenericMutationFn = ({
  record,
  calyxCountry,
}: {
  record: any;
  calyxCountry: string | null;
}) => updateLocationGenericMapping(record, calyxCountry);

function GenericLocationMapper() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');

  const {
    data: rows = [],
    isLoading: mappingsLoading,
    isFetching: mappingsFetching,
  } = useQuery({
    queryKey: ['locationGenericMappings'],
    queryFn: fetchLocationGenericMappingsData,
  });

  const {
    data: locations = [],
    isLoading: locationsLoading,
  } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocationsData,
  });

  const {
    mutate: updateLocationMapping,
    isLoading: isUpdating,
  } = useMutation(locationGenericMutationFn, {
    onSuccess: () => {
      queryClient.invalidateQueries(['locationGenericMappings']);
      message.success('Location generic mapping updated');
    },
    onError: () => {
      message.error('Failed to update location generic mapping. Please try again.');
    },
  });

  const loading = mappingsLoading || mappingsFetching || locationsLoading || isUpdating;

  const calyxCountryOptions = useMemo(() => {
    const raw = Array.isArray(locations) ? locations : (locations as any)?.response || [];
    const countries = (raw as any[]).filter((loc) => loc.type === 'country');
    return countries.map((loc) => ({
      value: loc.display_name || loc.name,
      label: loc.display_name || loc.name,
    }));
  }, [locations]);

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rows as any[]).filter((row) => {
      const status = getStatusFromCalyxCountry(row.calyx_country);
      if (statusFilter !== 'all' && status !== statusFilter) return false;

      if (!text) return true;

      const haystack = [
        row.registry_country || '',
        row.calyx_country || '',
        row.project_count != null ? String(row.project_count) : '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(text);
    });
  }, [rows, search, statusFilter]);

  const handleCalyxCountryChange = (record: any, value: string | null) => {
    const newValue = value ?? null;
    updateLocationMapping({ record, calyxCountry: newValue });
  };

  const columns: any[] = [
    {
      title: 'Registry Country',
      dataIndex: 'registry_country',
      key: 'registry_country',
      width: 200,
      sorter: (a: any, b: any) =>
        (a.registry_country || '').localeCompare(b.registry_country || ''),
    },
    {
      title: 'Calyx Country',
      dataIndex: 'calyx_country',
      key: 'calyx_country',
      width: 260,
      sorter: (a: any, b: any) =>
        (a.calyx_country || '').localeCompare(b.calyx_country || ''),
      render: (value: string | null, record: any) => {
        const baseOptions = calyxCountryOptions;
        const options =
          value && !baseOptions.some((o) => o.value === value)
            ? [{ value, label: value }, ...baseOptions]
            : baseOptions;

        return (
          <Select
            allowClear
            placeholder="Select Calyx Country"
            value={value || undefined}
            options={options}
            onChange={(val) => handleCalyxCountryChange(record, val ?? null)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: 'Project Count',
      dataIndex: 'project_count',
      key: 'project_count',
      width: 140,
      align: 'right',
      sorter: (a: any, b: any) => (a.project_count ?? 0) - (b.project_count ?? 0),
      render: (value: number | null) => (value != null ? value.toLocaleString() : '—'),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) =>
        getStatusFromCalyxCountry(a.calyx_country).localeCompare(
          getStatusFromCalyxCountry(b.calyx_country),
        ),
      render: (_: any, record: any) => {
        const status = getStatusFromCalyxCountry(record.calyx_country);
        return renderStatusTag(status);
      },
    },
  ];

  const needsMappingCount = (rows as any[]).filter(
    (r) => getStatusFromCalyxCountry(r.calyx_country) === 'needs-mapping',
  ).length;
  const mappedCount = (rows as any[]).length - needsMappingCount;

  return (
    <div
      style={{
        padding: 24,
        background: '#f5f5f5',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 auto' }}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Generic Country Mapper
          </Title>
          <Text type="secondary">
            Map registry country names to Calyx countries as a default fallback mapping.
          </Text>
        </div>
        <Alert
          type="info"
          showIcon
          closable
          message="Please note that changes saved here won't appear on the platform immediately. They will be applied during the next automatic registry sync, which runs every Saturday."
          style={{ width: '50%', flexShrink: 0 }}
        />
      </div>
      <Card
        bordered={false}
        style={{ borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
      >
        <Spin spinning={loading} tip="Loading location generic mappings…">
          <StatCards
            stats={[
              {
                label: 'Total Countries',
                value: (rows as any[]).length,
                color: '#8c8c8c',
                active: statusFilter === 'all',
                onClick: () => setStatusFilter('all'),
              },
              {
                label: 'Mapped',
                value: mappedCount,
                color: '#52c41a',
                active: statusFilter === 'mapped',
                onClick: () => setStatusFilter('mapped'),
              },
              {
                label: 'Needs Mapping',
                value: needsMappingCount,
                color: '#ff4d4f',
                active: statusFilter === 'needs-mapping',
                onClick: () => setStatusFilter('needs-mapping'),
              },
            ]}
          />

          <div
            style={{
              margin: '0 0 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search registry country, calyx country, project count…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 360 }}
            />
            <Space wrap>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  queryClient.invalidateQueries(['locationGenericMappings']);
                  queryClient.invalidateQueries(['locations']);
                }}
              >
                Refresh
              </Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={filteredRows}
            rowKey="id"
            size="middle"
            tableLayout="fixed"
            showSorterTooltip={false}
            pagination={{ defaultPageSize: 50, showSizeChanger: true }}
          />
        </Spin>
      </Card>
    </div>
  );
}

export default GenericLocationMapper;
